/**
 * Channel-specific component types and interfaces.
 *
 * This module defines the contracts for channel-specific UI components,
 * allowing different channels (Zapy/WhatsApp, Telegram, Email) to have
 * customized UI while maintaining a consistent interface.
 */

import type { Message, ChannelType, Conversation, EmailMessageMeta } from '../../../types/chat.types';

// =============================================================================
// EMAIL METADATA TYPES
// =============================================================================

/**
 * Re-export EmailMessageMeta as EmailMessageMetadata for backwards compatibility
 */
export type { EmailMessageMeta as EmailMessageMetadata } from '../../../types/chat.types';

/**
 * Extract email metadata from a message
 */
export function getEmailMetadata(message: Message): EmailMessageMeta | null {
  return message.metadata?.email ?? null;
}

// =============================================================================
// COMPOSER TYPES
// =============================================================================

/**
 * Base payload for sending a message (used by instant messaging channels)
 */
export interface BaseSendMessagePayload {
  body: string;
  attachments?: File[];
  replyToUuid?: string;
}

/**
 * Email-specific payload with additional fields
 */
export interface EmailSendMessagePayload extends BaseSendMessagePayload {
  subject?: string;
  to?: string[];
  cc?: string[];
  isHtml?: boolean;
}

/**
 * Union of all send message payloads
 */
export type SendMessagePayload = BaseSendMessagePayload | EmailSendMessagePayload;

/**
 * Type guard for email payload
 */
export function isEmailPayload(payload: SendMessagePayload): payload is EmailSendMessagePayload {
  return 'subject' in payload || 'to' in payload || 'cc' in payload;
}

/**
 * Props for channel-specific message composers
 */
export interface MessageComposerProps {
  conversationUuid: string;
  conversation: Conversation;
  onSendMessage: (message: SendMessagePayload) => Promise<void>;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
  disabled?: boolean;
  onTyping?: () => void;
  onStopTyping?: () => void;
  maxFileSizeBytes?: number;
  providerName?: string;
}

// =============================================================================
// MESSAGE BUBBLE TYPES
// =============================================================================

/**
 * Realtime reaction from WebSocket
 */
export interface RealtimeReaction {
  reaction: string;
  userId?: number;
  contactIdentityId?: number;
}

/**
 * Props for channel-specific message bubbles
 */
export interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  realtimeReactions?: RealtimeReaction[];
  currentUserId?: number;
  channelType?: ChannelType | null;
  onMessageUpdated?: () => void;
}

// =============================================================================
// HEADER TYPES
// =============================================================================

/**
 * Email-specific header info to display
 */
export interface EmailHeaderInfo {
  subject?: string;
  from?: string;
  to?: string[];
  cc?: string[];
  threadCount?: number;
}

/**
 * Props for channel-specific header extensions
 */
export interface ChannelHeaderExtensionProps {
  conversation: Conversation;
  messages?: Message[];
}

// =============================================================================
// CHANNEL CAPABILITIES
// =============================================================================

/**
 * Capabilities that differ between channels
 */
export interface ChannelCapabilities {
  /** Channel supports emoji reactions */
  canReact: boolean;
  /** Channel supports message editing */
  canEdit: boolean;
  /** Channel supports message deletion */
  canDelete: boolean;
  /** Channel supports message forwarding */
  canForward: boolean;
  /** Channel supports typing indicators */
  canShowTyping: boolean;
  /** Channel supports read receipts */
  canShowReadReceipts: boolean;
  /** Channel supports rich text/HTML content */
  supportsRichText: boolean;
  /** Channel supports threading */
  supportsThreading: boolean;
  /** Time limit for editing (null = no limit) */
  editTimeLimit: number | null;
  /** Time limit for deletion (null = no limit) */
  deleteTimeLimit: number | null;
}

/**
 * Get capabilities for a specific channel type
 */
export function getChannelCapabilities(channelType?: ChannelType | null): ChannelCapabilities {
  switch (channelType) {
    case 'zapy':
      return {
        canReact: true,
        canEdit: true,
        canDelete: true,
        canForward: true,
        canShowTyping: true,
        canShowReadReceipts: true,
        supportsRichText: false,
        supportsThreading: false,
        editTimeLimit: 15 * 60 * 1000, // 15 minutes
        deleteTimeLimit: 15 * 60 * 1000,
      };
    case 'telegram':
      return {
        canReact: true,
        canEdit: true,
        canDelete: true,
        canForward: true,
        canShowTyping: true,
        canShowReadReceipts: true,
        supportsRichText: false,
        supportsThreading: false,
        editTimeLimit: null, // No limit
        deleteTimeLimit: null,
      };
    case 'email':
      return {
        canReact: false,
        canEdit: false, // Emails cannot be edited after sent
        canDelete: false, // Emails cannot be deleted from recipient
        canForward: false,
        canShowTyping: false, // Email doesn't have typing indicators
        canShowReadReceipts: false, // Limited read receipt support
        supportsRichText: true, // HTML emails
        supportsThreading: true, // Email threading via headers
        editTimeLimit: null,
        deleteTimeLimit: null,
      };
    default:
      return {
        canReact: false,
        canEdit: false,
        canDelete: false,
        canForward: false,
        canShowTyping: false,
        canShowReadReceipts: false,
        supportsRichText: false,
        supportsThreading: false,
        editTimeLimit: null,
        deleteTimeLimit: null,
      };
  }
}

// =============================================================================
// CHANNEL UI CONFIGURATION
// =============================================================================

/**
 * UI color schemes for different channels
 */
export interface ChannelColorScheme {
  light: {
    outgoing: string;
    outgoingHover: string;
    incoming: string;
    incomingHover: string;
    statusIcon: string;
    statusIconRead: string;
    timestamp: string;
  };
  dark: {
    outgoing: string;
    outgoingHover: string;
    incoming: string;
    incomingHover: string;
    statusIcon: string;
    statusIconRead: string;
    timestamp: string;
  };
}

/**
 * WhatsApp-style colors (used for Zapy, Telegram)
 */
export const INSTANT_MESSAGE_COLORS: ChannelColorScheme = {
  light: {
    outgoing: '#d9fdd3',
    outgoingHover: '#c8edc1',
    incoming: '#ffffff',
    incomingHover: '#f5f5f5',
    statusIcon: '#667781',
    statusIconRead: '#53bdeb',
    timestamp: '#667781',
  },
  dark: {
    outgoing: '#005c4b',
    outgoingHover: '#004d40',
    incoming: '#202c33',
    incomingHover: '#2a3942',
    statusIcon: '#8696a0',
    statusIconRead: '#53bdeb',
    timestamp: '#8696a0',
  },
};

/**
 * Email-style colors (more formal, inbox-like)
 */
export const EMAIL_COLORS: ChannelColorScheme = {
  light: {
    outgoing: '#e3f2fd', // Light blue for sent
    outgoingHover: '#bbdefb',
    incoming: '#fafafa', // Very light gray for received
    incomingHover: '#f0f0f0',
    statusIcon: '#757575',
    statusIconRead: '#2196f3',
    timestamp: '#9e9e9e',
  },
  dark: {
    outgoing: '#1a237e', // Dark blue
    outgoingHover: '#283593',
    incoming: '#303030',
    incomingHover: '#424242',
    statusIcon: '#b0bec5',
    statusIconRead: '#64b5f6',
    timestamp: '#9e9e9e',
  },
};

/**
 * Get color scheme for a channel type
 */
export function getChannelColors(channelType?: ChannelType | null): ChannelColorScheme {
  switch (channelType) {
    case 'email':
      return EMAIL_COLORS;
    case 'zapy':
    case 'telegram':
    default:
      return INSTANT_MESSAGE_COLORS;
  }
}
